import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";
import { EvidoxConfiguration } from "@/models/EvidoxConfiguration";

import { settingsService } from "@/services";

import { useBrandStore } from "@/stores/brand-store";
import { useLoggingStore } from "@/stores/logging-store";

import type { ClientSetting } from "@/models/ClientSetting";
import type { PredefinedSearch } from "@/models/SearchRequest";

interface State {
  moduleStatus: ModuleStatus;
  predefinedSearch: PredefinedSearch;
  scaleFactor: number;
}
export const useSettingsStore = defineStore("settings", {
  scope: "global",
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    predefinedSearch: { terms: [] },
    scaleFactor: 1,
  }),
  getters: {},
  actions: {
    async load(): Promise<void> {
      const selectedClient = useBrandStore().selectedClient;
      if (selectedClient == null) return;

      if (this.moduleStatus !== ModuleStatus.Initial) return;

      // Try and load the settings into here
      try {
        // The api catches errors and logs them, so we don't need to do anything here
        const [clientSettings, evidoxConfiguration] = await Promise.allSettled([
          settingsService.settings(selectedClient),
          settingsService.evidox(),
        ]);

        if (clientSettings.status === "rejected" && evidoxConfiguration.status === "rejected") {
          if (import.meta.env.MODE !== "test") {
            console.error(clientSettings.reason);
            console.error(evidoxConfiguration.reason);
          }

          throw `Failed to load ${selectedClient} and Evidox settings`;
        }

        if (clientSettings.status === "fulfilled") this.clientSettingsLoaded(clientSettings.value);
        if (evidoxConfiguration.status === "fulfilled") this.genericSettingsLoaded(evidoxConfiguration.value);
      } catch (error: any) {
        console.error(error);

        useLoggingStore().Error({
          message: "Failed to load settings into module",
          data: error as object,
        });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },

    // Mutations
    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    clientSettingsLoaded(loadedSettings: ClientSetting): void {
      loadedSettings.globalSearch.terms
        .filter((term) => term.searchTermIsRegex)
        .forEach((term) => {
          const regex = term.userDefinedRegex
            .split("\\")
            .filter((m) => m.length > 0)
            .join("\\");
          term.userDefinedRegex = `${term.userDefinedRegex.startsWith("\\") ? "\\" : ""}${regex}${
            term.userDefinedRegex.endsWith("\\") ? "\\" : " "
          }`.trim();
        });

      this.predefinedSearch = loadedSettings.globalSearch as unknown as PredefinedSearch;
    },
    genericSettingsLoaded(loadedSettings: EvidoxConfiguration): void {
      this.scaleFactor = loadedSettings.scaleFactor;
    },
  },
});
